import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAlert } from "./AlertContext";

const DatasetContext = createContext();

export const DatasetProvider = ({ children }) => {
  const { showAlert } = useAlert();

  const [datasets, setDatasets] = useState([]);
  const [selectedDatasetId, setSelectedDatasetId] = useState(null);
  const [selectedImageId, setSelectedImageId] = useState(null);
  const [isLoadingDatasets, setIsLoadingDatasets] = useState(true);
  const [isCreatingDataset, setIsCreatingDataset] = useState(false);
  const [isStartingDataset, setIsStartingDataset] = useState(false);
  const [isSavingImage, setIsSavingImage] = useState(false);
  const [isCompletingDataset, setIsCompletingDataset] = useState(false);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isDiscardChangesModalOpen, setIsDiscardChangesModalOpen] = useState(false);

  const pendingNavigationRef = useRef(null);

  const loadDatasets = async () => {
    const token = localStorage.getItem('token');

    const response = await fetch(process.env.REACT_APP_API_ADDRESS + 'datasets', {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok)
      throw new Error(`Unable to load datasets. Status: ${response.status}`);

    const data = await response.json();
    setDatasets(data);

    return data;
  };

  useEffect(() => {
    const initializeDatasets = async () => {
      setIsLoadingDatasets(true);

      try {
        await loadDatasets();
      } catch (error) {
        console.error('Unable to load datasets:', error);
        showAlert('error', 'Oops! Something went wrong while attempting to load the datasets.');
      } finally {
        setIsLoadingDatasets(false);
      }
    };

    initializeDatasets();
  }, []);

  const selectedDataset = useMemo(() => {
    if (selectedDatasetId === null) return null;
    return datasets.find((dataset) => dataset.id === selectedDatasetId) ?? null;
  }, [datasets, selectedDatasetId]);

  const selectedImage = useMemo(() => {
    if (selectedDataset === null || selectedImageId === null) return null;
    return selectedDataset.dataset_images?.find((image) => image.id === selectedImageId) ?? null;
  }, [selectedDataset, selectedImageId]);

  useEffect(() => {
    if (selectedDataset === null || selectedDataset.state === 'waiting_capture') {
      setSelectedImageId(null);
      return;
    }

    const imageExists = selectedImageId !== null &&
      selectedDataset.dataset_images?.some((image) => image.id === selectedImageId);

    if (imageExists) return;

    if (selectedDataset.state === 'completed') {
      setSelectedImageId(null);
      return;
    }

    const sortedImages = [...(selectedDataset.dataset_images ?? [])].sort((imageA, imageB) => imageA.id - imageB.id);
    const firstUnseenImage = sortedImages.find((image) => image.state === 'unseen');

    setSelectedImageId(firstUnseenImage?.id ?? null);
  }, [selectedDataset, selectedImageId]);

  const requestNavigation = (action) => {
    if (isSavingImage) return;

    if (!hasUnsavedChanges) {
      action();
      return;
    }

    pendingNavigationRef.current = action;
    setIsDiscardChangesModalOpen(true);
  };

  const selectDataset = (datasetId) => {
    if (datasetId === selectedDatasetId) return;

    requestNavigation(() => {
      setHasUnsavedChanges(false);
      setSelectedImageId(null);
      setSelectedDatasetId(datasetId);
    });
  };

  const selectImage = (imageId) => {
    if (imageId === selectedImageId) return;

    requestNavigation(() => {
      setHasUnsavedChanges(false);
      setSelectedImageId(imageId);
    });
  };

  const confirmDiscardChanges = () => {
    const pendingNavigation = pendingNavigationRef.current;

    pendingNavigationRef.current = null;
    setHasUnsavedChanges(false);
    setIsDiscardChangesModalOpen(false);
    pendingNavigation?.();
  };

  const cancelDiscardChanges = () => {
    pendingNavigationRef.current = null;
    setIsDiscardChangesModalOpen(false);
  };

  const createDataset = async (name) => {
    setIsCreatingDataset(true);

    try {
      const token = localStorage.getItem('token');

      const response = await fetch(process.env.REACT_APP_API_ADDRESS + 'datasets', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          creation_date: Math.floor(Date.now() / 1000),
        }),
      });

      if (!response.ok)
        throw new Error(`Unable to create dataset. Status: ${response.status}`);

      const createdDataset = await response.json();

      await loadDatasets();
      setSelectedImageId(null);
      setSelectedDatasetId(createdDataset.id);
      showAlert('success', 'Le dossier a été créé avec succès.');

      return createdDataset;
    } catch (error) {
      console.error('Unable to create dataset:', error);
      showAlert('error', 'Une erreur est survenue lors de la création du dossier.');
      throw error;
    } finally {
      setIsCreatingDataset(false);
    }
  };

  const startDataset = async (datasetId) => {
    setIsStartingDataset(true);

    try {
      const token = localStorage.getItem('token');

      const response = await fetch(process.env.REACT_APP_API_ADDRESS + `datasets/${datasetId}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/merge-patch+json',
        },
        body: JSON.stringify({ start_date: Math.floor(Date.now() / 1000) }),
      });

      if (!response.ok)
        throw new Error(`Unable to start dataset. Status: ${response.status}`);

      const updatedDataset = await response.json();

      setDatasets((currentDatasets) => currentDatasets.map((dataset) => dataset.id === updatedDataset.id ? updatedDataset : dataset));
      setSelectedImageId(null);
      showAlert('success', 'L’annotation du dossier a démarré.');

      return updatedDataset;
    } catch (error) {
      console.error('Unable to start dataset:', error);
      showAlert('error', 'Une erreur est survenue lors du démarrage de l’annotation.');
      throw error;
    } finally {
      setIsStartingDataset(false);
    }
  };

  const validateImage = async (annotationData) => {
    if (selectedDataset === null || selectedImage === null) return;

    setIsSavingImage(true);

    try {
      const token = localStorage.getItem('token');

      const response = await fetch(process.env.REACT_APP_API_ADDRESS + `dataset_images/${selectedImage.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/merge-patch+json',
        },
        body: JSON.stringify({
          state: 'validated',
          annotation_data: annotationData,
        }),
      });

      if (!response.ok)
        throw new Error(`Unable to validate image. Status: ${response.status}`);

      const updatedImage = await response.json();
      const updatedImages = (selectedDataset.dataset_images ?? []).map((image) => image.id === updatedImage.id ? updatedImage : image);
      const nextImage = [...updatedImages].sort((imageA, imageB) => imageA.id - imageB.id).find((image) => image.state === 'unseen');

      setDatasets((currentDatasets) => currentDatasets.map((dataset) => {
        if (dataset.id !== selectedDataset.id) return dataset;

        return {
          ...dataset,
          dataset_images: updatedImages,
          viewed_images: updatedImages.filter((image) => image.state !== 'unseen').length,
          validated_images: updatedImages.filter((image) => image.state === 'validated').length,
          skipped_images: updatedImages.filter((image) => image.state === 'skipped').length,
        };
      }));

      setHasUnsavedChanges(false);
      setSelectedImageId(nextImage?.id ?? null);

      return updatedImage;
    } catch (error) {
      console.error('Unable to validate image:', error);
      showAlert('error', 'Une erreur est survenue lors de la validation de l’image.');
      throw error;
    } finally {
      setIsSavingImage(false);
    }
  };

  const skipImage = async () => {
    if (selectedDataset === null || selectedImage === null) return;

    if (selectedImage.state !== 'unseen') {
      const nextImage = [...(selectedDataset.dataset_images ?? [])]
        .sort((imageA, imageB) => imageA.id - imageB.id)
        .find((image) => image.state === 'unseen');

      setSelectedImageId(nextImage?.id ?? null);
      return;
    }

    setIsSavingImage(true);

    try {
      const token = localStorage.getItem('token');

      const response = await fetch(process.env.REACT_APP_API_ADDRESS + `dataset_images/${selectedImage.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/merge-patch+json',
        },
        body: JSON.stringify({
          state: 'skipped',
          annotation_data: selectedImage.annotation_data,
        }),
      });

      if (!response.ok)
        throw new Error(`Unable to skip image. Status: ${response.status}`);

      const updatedImage = await response.json();
      const updatedImages = (selectedDataset.dataset_images ?? []).map((image) => image.id === updatedImage.id ? updatedImage : image);
      const nextImage = [...updatedImages].sort((imageA, imageB) => imageA.id - imageB.id).find((image) => image.state === 'unseen');

      setDatasets((currentDatasets) => currentDatasets.map((dataset) => {
        if (dataset.id !== selectedDataset.id) return dataset;

        return {
          ...dataset,
          dataset_images: updatedImages,
          viewed_images: updatedImages.filter((image) => image.state !== 'unseen').length,
          validated_images: updatedImages.filter((image) => image.state === 'validated').length,
          skipped_images: updatedImages.filter((image) => image.state === 'skipped').length,
        };
      }));

      setHasUnsavedChanges(false);
      setSelectedImageId(nextImage?.id ?? null);

      return updatedImage;
    } catch (error) {
      console.error('Unable to skip image:', error);
      showAlert('error', 'Une erreur est survenue lors du passage à l’image suivante.');
      throw error;
    } finally {
      setIsSavingImage(false);
    }
  };

  const markImageAsUnseen = async () => {
    if (selectedDataset === null || selectedImage === null || !['validated', 'skipped'].includes(selectedImage.state)) return;

    setIsSavingImage(true);

    try {
      const token = localStorage.getItem('token');

      const response = await fetch(process.env.REACT_APP_API_ADDRESS + `dataset_images/${selectedImage.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/merge-patch+json',
        },
        body: JSON.stringify({ state: 'unseen' }),
      });

      if (!response.ok)
        throw new Error(`Unable to mark image as unseen. Status: ${response.status}`);

      const updatedImage = await response.json();
      const updatedImages = (selectedDataset.dataset_images ?? []).map((image) => image.id === updatedImage.id ? updatedImage : image);

      setDatasets((currentDatasets) => currentDatasets.map((dataset) => {
        if (dataset.id !== selectedDataset.id) return dataset;

        return {
          ...dataset,
          dataset_images: updatedImages,
          viewed_images: updatedImages.filter((image) => image.state !== 'unseen').length,
          validated_images: updatedImages.filter((image) => image.state === 'validated').length,
          skipped_images: updatedImages.filter((image) => image.state === 'skipped').length,
        };
      }));

      setHasUnsavedChanges(false);
      showAlert('success', 'L’image a été marquée comme non traitée.');

      return updatedImage;
    } catch (error) {
      console.error('Unable to mark image as unseen:', error);
      showAlert('error', 'Une erreur est survenue lors de la modification de l’image.');
      throw error;
    } finally {
      setIsSavingImage(false);
    }
  };

  const requestMarkImageAsUnseen = () => {
    requestNavigation(() => {
      setHasUnsavedChanges(false);
      markImageAsUnseen();
    });
  };

  const requestSkipImage = () => {
    requestNavigation(() => {
      setHasUnsavedChanges(false);
      skipImage();
    });
  };

  const completeDataset = async () => {
    if (selectedDataset === null) return;

    setIsCompletingDataset(true);

    try {
      const token = localStorage.getItem('token');

      const response = await fetch(process.env.REACT_APP_API_ADDRESS + `datasets/${selectedDataset.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/merge-patch+json',
        },
        body: JSON.stringify({ complete_date: Math.floor(Date.now() / 1000) }),
      });

      if (!response.ok)
        throw new Error(`Unable to complete dataset. Status: ${response.status}`);

      const updatedDataset = await response.json();

      setDatasets((currentDatasets) => currentDatasets.map((dataset) => dataset.id === updatedDataset.id ? updatedDataset : dataset));
      setSelectedImageId(null);
      setHasUnsavedChanges(false);
      showAlert('success', 'Le dossier a été terminé avec succès.');

      return updatedDataset;
    } catch (error) {
      console.error('Unable to complete dataset:', error);
      showAlert('error', 'Une erreur est survenue lors de la validation du dossier.');
      throw error;
    } finally {
      setIsCompletingDataset(false);
    }
  };

  const contextValue = useMemo(
    () => ({
      datasets,
      selectedDataset,
      selectedDatasetId,
      selectedImage,
      selectedImageId,
      isLoadingDatasets,
      isCreatingDataset,
      isStartingDataset,
      isSavingImage,
      hasUnsavedChanges,
      isDiscardChangesModalOpen,
      selectDataset,
      selectImage,
      createDataset,
      startDataset,
      validateImage,
      skipImage,
      requestSkipImage,
      setHasUnsavedChanges,
      confirmDiscardChanges,
      cancelDiscardChanges,
      isCompletingDataset,
      completeDataset,
      markImageAsUnseen,
      requestMarkImageAsUnseen,
    }),
    [
      datasets,
      selectedDataset,
      selectedDatasetId,
      selectedImage,
      selectedImageId,
      isLoadingDatasets,
      isCreatingDataset,
      isStartingDataset,
      isSavingImage,
      hasUnsavedChanges,
      isDiscardChangesModalOpen,
      isCompletingDataset,
    ],
  );
  return <DatasetContext.Provider value={contextValue}>{children}</DatasetContext.Provider>;
};

export const useDataset = () => {
  return useContext(DatasetContext);
};